import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import NotFoundError from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/NotFoundError';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import usePortfolioUserMemberUpdateRoleMutation from 'src/mutations/PortfolioUserMemberUpdateRoleMutation';
import FlashesStore from 'src/stores/FlashesStore';
import { PORTFOLIO_USER_ROLE_OWNER } from 'src/util/constants';
import displayUserName from 'src/util/i18n/decorators/displayUserName';

import Form from './Form';

const AdminPortfolioUserMemberEditFragment = graphql`
fragment AdminPortfolioUserMemberEdit_portfolioUserMember on PortfolioUserMember @throwOnFieldError {
  uuid
  portfolio {
    uuid
    title
    userMembers {
      edges {
        node {
          uuid
          role
        }
      }
    }
  }
  role
  user {
    email
    givenName
    familyName
  }
}
`;

/**
 * Admin form for changing a portfolio user member's role. Field errors, and a
 * resolved query without a member, throw to the route's PlaceholderErrorBoundary.
 * @param {object} props - Component props.
 * @param {object|null} props.portfolioUserMember - Relay fragment reference for the member.
 * @param {object} props.router - Found router, used to open the member after editing.
 * @returns {React.ReactComponentElement} - AdminPortfolioUserMemberEdit component
 */
function AdminPortfolioUserMemberEdit({ portfolioUserMember: memberRef = null, router }) {
  const portfolioUserMember = useFragment(AdminPortfolioUserMemberEditFragment, memberRef);
  const [processing, setProcessing] = useState(false);
  const intl = useIntl();
  const portfolioUserMemberUpdateRole = usePortfolioUserMemberUpdateRoleMutation();

  if (!portfolioUserMember) {
    // A completed query without a member would otherwise never leave the spinner.
    throw new NotFoundError('AdminPortfolioUserMemberEdit: the portfolio user member query returned no member');
  }

  const {
    user, uuid: userMemberUuid, portfolio, role,
  } = portfolioUserMember;
  const { title: portfolioTitle, uuid: portfolioUuid, userMembers } = portfolio || {};
  const { email } = user || {};
  let userMemberUsername = displayUserName(intl, user);
  userMemberUsername = (email && userMemberUsername !== email) ? `${userMemberUsername} (${email})` : userMemberUsername;

  const userMembersRoles = userMembers?.edges?.map((edge) => edge?.node?.role);
  const portfolioHasOwner = userMembersRoles?.includes(PORTFOLIO_USER_ROLE_OWNER);
  const pageTitle = intl.formatMessage({ id: 'admin.admin_portfolio_user_member.admin_portfolio_user_member_edit.page.title', defaultMessage: 'Enosi Admin - Edit portfolio user member: {userMemberUsername}' }, { userMemberUsername });
  const breadcrumbAdmin = intl.formatMessage({ id: 'admin.admin_portfolio_user_member.admin_portfolio_user_member_edit.breadcrumbs.admin.label', defaultMessage: 'Admin' });
  const breadcrumbPortfolios = intl.formatMessage({ id: 'admin.admin_portfolio_user_member.admin_portfolio_user_member_edit.breadcrumbs.portfolios.label', defaultMessage: 'Portfolios' });
  const breadcrumbPortfolioUserMembers = intl.formatMessage({ id: 'admin.admin_portfolio_user_member.admin_portfolio_user_member_edit.breadcrumbs.portfolio_user_members.label', defaultMessage: 'Portfolio user members' });
  const breadcrumbsPortfolioUserMemberEdit = intl.formatMessage({ id: 'admin.admin_portfolio_user_member.admin_portfolio_user_member_edit.breadcrumbs.portfolio_property_member', defaultMessage: "Edit portfolio user member's role" });
  const mutationName = 'portfolioUserMemberUpdateRole';

  const formSuccessHandler = (response, errors) => {
    setProcessing(false);

    if (errors && errors.length > 0) {
      errors.forEach((error) => FlashesStore.flash(FlashesStore.ERROR, {
        message: FlashesStore.ERROR,
        source: {
          errors: [error],
        },
      }, mutationName));
      return;
    }

    const { uuid: editedUserId } = response.portfolioUserMemberUpdateRole;

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      <span>
        {intl.formatMessage({ id: 'admin.admin_portfolio_user_member.admin_portfolio_user_member_edit.form.response_message.success', defaultMessage: 'Successfully edited portfolio user member {uuid}.' }, {
          uuid: editedUserId,
        })}
      </span>,
      mutationName,
    );
    router.push(`/admin/portfolio-user-members/${editedUserId}`);
  };

  const formFailureHandler = (error) => {
    setProcessing(false);
    FlashesStore.flash(FlashesStore.ERROR, error, mutationName);
  };

  const handleEdit = (input) => {
    setProcessing(true);
    portfolioUserMemberUpdateRole(
      userMemberUuid,
      input,
      formSuccessHandler,
      formFailureHandler,
    );
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbPortfolios, path: '/admin/portfolios' },
          { name: `${portfolioTitle}`, path: `/admin/portfolios/${portfolioUuid}` },
          { name: breadcrumbPortfolioUserMembers },
          { name: userMemberUsername, path: `/admin/portfolio-user-members/${userMemberUuid}` },
          { name: breadcrumbsPortfolioUserMemberEdit },
        ]}
      />
      <Form
        handleEdit={handleEdit}
        portfolioHasOwner={portfolioHasOwner}
        processing={processing}
        role={role}
      />
    </>
  );
}

export default AdminPortfolioUserMemberEdit;

AdminPortfolioUserMemberEdit.propTypes = {
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  portfolioUserMember: PropTypes.object,
};
